import { FunctionComponent, ReactNode, forwardRef, Ref, PropsWithoutRef } from 'react';
import styled, { css } from 'styled-components';
import { rgba as polishedRgba } from 'polished';

import { BaseProps, ForwardProps, AsProp, PropsWithDefaults } from '../../types';
import { defaultThemeProp } from '../../theme';
import { tryCatch } from '../../utils';
import { calculateFontSize, FontSize } from '../../styles';

export interface TextProps extends BaseProps, AsProp {
  /** Text content. */
  children: ReactNode;
  /**
   * Typographical sizes and styles.
   * @default "primary"
   */
  variant?: 'primary' | 'secondary' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /**
   * Render text with semantic color based on a status.
   */
  status?: 'error' | 'warning' | 'success';
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLSpanElement | HTMLHeadingElement>;
}

type TextPropsWithDefaults = PropsWithDefaults<TextProps, 'variant'>;

export const StyledText = styled.span<TextPropsWithDefaults>(props => {
  const {
    variant,
    status,
    theme: {
      base: {
        'font-size': fontSize,
        'font-scale': fontScale,
        palette: { 'foreground-color': foregroundColor, urgent: error, warn: warning, success },
        transparency: { 'transparent-2': secondaryAlpha }
      },
      components: { text }
    }
  } = props;

  let color: string | undefined;

  if (status) {
    color = {
      error,
      warning,
      success
    }[status];
  }

  if (variant === 'secondary') {
    color = tryCatch(() => polishedRgba(color ?? foregroundColor, secondaryAlpha));
  }

  const fontSizes = calculateFontSize(fontSize, fontScale);

  return css`
    font-size: ${fontSizes[text[variant]['font-size'] as FontSize]};
    font-weight: ${text[variant]['font-weight']};
    color: ${color};
  `;
});

StyledText.defaultProps = defaultThemeProp;

const Text: FunctionComponent<TextProps & ForwardProps> = forwardRef(
  (
    { variant = 'primary', as, ...restProps }: PropsWithoutRef<TextProps>,
    ref: TextProps['ref']
  ) => {
    // If variant is for a heading, and no as is passed, set as to heading tag.
    if (!as && /h\d/i.test(variant)) as = variant as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    return <StyledText ref={ref} variant={variant} as={as} {...restProps} />;
  }
);

export default Text;
